// Castintech dashboard — Pano (Kanban) view modulu.
// Faz 9b PR7 — pano view + new-issue modal:
// - Kanban: KANBAN_COLS, filteredTasks, applyFilters, clearFilters,
//           renderAssigneeFilter, renderKanban, issueCard
// - Agent dispatch + Yeni Görev modal: openNewIssue, dispatchToAgent,
//   updateAgentDropdown, closeNewIssue, handleModalOverlayClick, submitNewIssue
//
// State erisimi: state.js direct import (state.tasks, state.companies, vs.)
// Utils: utils.js (escHtml/escAttr/ago/normStatus/getGem)
// Api: api.js (postIssue)
//
// Inline call-site'lar (HTML onclick + agent drawer) bridge.js araciligi ile
// window.X erisir: openDetail, openAgentDrawer (drawer henuz module'e tasinmadi),
// toast (UI helper), refresh (data layer).

import { state } from '../state.js';
import * as Api from '../api.js';
import { escHtml, escAttr, ago, normStatus, getGem, compareTasksStable, $, asEl } from '../utils.js';
import { toast } from '../ui/toast.js';
import { openDetail } from '../ui/detail-panel.js';
import { toggleSuggestions } from '../ui/suggestions.js';
import { refresh, navigate } from '../main.js';
import type { Issue, Agent } from '../../types/api';

// Faz 47.2 S2.A — Blocked card'larda son comment'ten "neden bloklu" inline
// snippet. Backend `/issues/blocked-reasons` 60s cache. İlk render hemen
// (snippet yok, sonradan async olarak gelir + re-render).
//
// Faz 49.1 hotfix — sonsuz döngü düzeltmesi:
//   Önceki kodda renderKanban → ensureBlockedReasonsLoaded → fetch →
//   queueMicrotask(renderKanban) → ensureBlockedReasonsLoaded → fetch ...
//   sonsuz zinciri vardı (frontend cache yoktu, _blockedReasonsFetchInflight
//   finally'de null'a düşüyordu, bir sonraki render hemen tekrar fetch'e
//   tetiklenirdi). Network log'da 30+ kez aynı URL çağrısı + backend N+1
//   Paperclip comment fetch yığılması → Cloudflare connection pool tükeniyor
//   → fetchAll ara sıra timeout → "Bağlantı hatası" toast.
//
// Çözüm: 60s frontend cache (backend cache ile hizalı). Re-render flag tabanlı
// (her resolve'da değil, sadece veri değişince).
const BLOCKED_REASONS_CACHE_TTL = 60_000;
let _blockedReasons: Record<string, string> = {};
let _blockedReasonsLastFetch = 0;
let _blockedReasonsFetchInflight: Promise<void> | null = null;

// Faz 52 — Done kolonu lazy load flag. Faz 51 pagination + Faz 52 excludeDone
// initial fetch sonrası done kolonu boş başlar. Kullanıcı "Yükle" tıklayınca
// getCompanyIssuesDone() per-company çağrılır + state.tasks'a merge edilir.
let _doneLoaded = false;
let _doneLoadInflight = false;
function ensureBlockedReasonsLoaded(): void {
  if (_blockedReasonsFetchInflight) return;
  if (Date.now() - _blockedReasonsLastFetch < BLOCKED_REASONS_CACHE_TTL) return;
  _blockedReasonsFetchInflight = (async () => {
    try {
      const res = await fetch('/castintech-api/issues/blocked-reasons', { credentials: 'include' });
      _blockedReasonsLastFetch = Date.now(); // 4xx/5xx olsa bile pencereyi aç (5xx için backoff yok ama spam'i kes)
      if (!res.ok) return;
      const data = await res.json() as { reasons?: Record<string, string> };
      if (data && data.reasons) {
        const prevKeys = Object.keys(_blockedReasons).sort().join(',');
        const nextKeys = Object.keys(data.reasons).sort().join(',');
        _blockedReasons = data.reasons;
        // Sadece veri farklıysa re-render. Aynıysa microtask + re-render
        // gerekmez (ki bu sonsuz döngünün asıl tetikleyicisiydi).
        if (prevKeys !== nextKeys && state.currentPage === 'pano') {
          queueMicrotask(() => { try { renderKanban(); } catch { /* ignore */ } });
        }
      }
    } catch { /* fail soft */ }
    finally { _blockedReasonsFetchInflight = null; }
  })();
}

interface NewIssueOpts {
  companyId: string;
  agentId: string;
  agentName: string;
}

// ── KANBAN sutunlari ─────────────────────────────────────────────────

export const KANBAN_COLS = [
  { id: 'backlog',     label: 'Backlog',    dotCls: 'col-dot-backlog' },
  { id: 'todo',        label: 'Bekliyor',   dotCls: 'col-dot-todo' },
  { id: 'in_progress', label: 'Devam',      dotCls: 'col-dot-progress' },
  { id: 'blocked',     label: 'Bloklu',     dotCls: 'col-dot-blocked' },
  { id: 'done',        label: 'Tamamlandı', dotCls: 'col-dot-done' },
];

// ── Filtre + selektor logici ─────────────────────────────────────────

// Faz 15 perf-opt: aMap buildup helper — renderKanban + filteredTasks
// arasinda paylasilabilsin diye exported.
function buildAgentMap(): Record<string, Agent> {
  const aMap: Record<string, Agent> = {};
  state.agents.forEach(a => { if (a.id) aMap[a.id] = a; });
  return aMap;
}

export function filteredTasks(aMap?: Record<string, Agent>): Issue[] {
  // Faz 15 perf-opt: 4-7 ardisik ts = ts.filter(...) → tek pass single filter.
  // Onceki versiyonda her aktif filtre yeni array kopyasi yaratiyordu.
  const tasks = state.tasks;
  const fs = state.filterState;
  const selCo = state.selectedCompanyId;
  const agFilter = state.agentFilter;
  const q = fs.search ? fs.search.toLowerCase() : '';
  const prio = fs.priority;
  const assignee = fs.assignee;
  const ageKey = fs.age;
  const cutoff = ageKey
    ? ({ today: 86400000, '3d': 3 * 86400000, '7d': 7 * 86400000 } as Record<string, number>)[ageKey]
    : 0;
  const now = ageKey && cutoff ? Date.now() : 0;
  // aMap sadece search filtresi aktifken gerekli — renderKanban'dan
  // share edilmis aMap varsa yeniden insa edilmiyor.
  const map = q ? (aMap || buildAgentMap()) : null;

  return tasks.filter(t => {
    if (selCo !== 'all' && t.company_id !== selCo) return false;
    const tAssignee = t.assigneeAgentId || t.assignee_id;
    if (agFilter && tAssignee !== agFilter) return false;
    if (q) {
      const a = map![tAssignee || ''];
      const aName = a ? (getGem(a.name).name || a.name || '').toLowerCase() : '';
      if (!(t.title || '').toLowerCase().includes(q)
          && !(t.description || '').toLowerCase().includes(q)
          && !aName.includes(q)) return false;
    }
    if (prio && (t.priority || '').toLowerCase() !== prio) return false;
    if (assignee && tAssignee !== assignee) return false;
    if (ageKey && cutoff) {
      const taskTime = new Date(t.updatedAt || t.created_at || now).getTime();
      const age = now - taskTime;
      if (ageKey === 'today') {
        if (!(age >= 0 && age < cutoff)) return false;
      } else {
        if (age < cutoff) return false;
      }
    }
    return true;
  });
}

// Faz 50.A — filter state'i localStorage'a yaz (private mode safe).
function persistFilterState(): void {
  try { localStorage.setItem('castintech_filter_state', JSON.stringify(state.filterState)); }
  catch { /* private mode / quota */ }
}

export function applyFilters(): void {
  state.filterState.search = ($<HTMLInputElement>('kf-search')?.value || '').trim();
  state.filterState.priority = $<HTMLSelectElement>('kf-priority')?.value || '';
  state.filterState.assignee = $<HTMLSelectElement>('kf-assignee')?.value || '';
  state.filterState.age = $<HTMLSelectElement>('kf-age')?.value || '';
  persistFilterState();
  if (state.currentPage === 'pano') renderKanban();
}

export function clearFilters(): void {
  state.filterState = { search: '', priority: '', assignee: '', age: '' };
  persistFilterState();
  (['kf-search', 'kf-priority', 'kf-assignee', 'kf-age'] as const).forEach(id => {
    const el = $<HTMLInputElement | HTMLSelectElement>(id);
    if (el) el.value = '';
  });
  if (state.currentPage === 'pano') renderKanban();
}

// Faz 50.A — Render boot'unda saved filter inputs UI'a yansıt (state.filterState
// localStorage'tan zaten restore edildi). DOMContentLoaded sonrası bir kez çağır.
export function restoreFilterUI(): void {
  const fs = state.filterState;
  const apply = (id: string, val: string) => {
    const el = $<HTMLInputElement | HTMLSelectElement>(id);
    if (el && val) el.value = val;
  };
  apply('kf-search', fs.search);
  apply('kf-priority', fs.priority);
  apply('kf-assignee', fs.assignee);
  apply('kf-age', fs.age);
}

export function renderAssigneeFilter(): void {
  const sel = $<HTMLSelectElement>('kf-assignee');
  if (!sel) return;
  const cur = sel.value;
  sel.innerHTML = '<option value="">Ajan (tümü)</option>'
    + state.agents.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(a => {
      const gem = getGem(a.name);
      return `<option value="${a.id}">${gem.icon || '◆'} ${gem.name || a.name}</option>`;
    }).join('');
  sel.value = cur;
}

// ── Yaklaşan/aktif toplantı bilgi şeridi ─────────────────────────────
// Pano üstünde ince bir bilgi satırı: aktif toplantı varsa onu, yoksa en
// yakın planlı (scheduled) toplantıyı gösterir. Veri kaynağı meetings API
// (status=scheduled,active,voting). 60s frontend cache (blocked-reasons
// pattern'i ile aynı — spam'i keser). Tıklanınca meetings view'a gider.

interface PanoMeeting {
  id: string;
  type?: string;
  title: string;
  status: string;          // scheduled | active | voting | completed | cancelled
  scheduled_start: string; // ISO
  actual_start?: string;
}

const PANO_MEETING_CACHE_TTL = 60_000;
let _panoMeetings: PanoMeeting[] = [];
let _panoMeetingLastFetch = 0;
let _panoMeetingFetchInflight: Promise<void> | null = null;

function ensurePanoMeetingLoaded(): void {
  if (_panoMeetingFetchInflight) return;
  if (Date.now() - _panoMeetingLastFetch < PANO_MEETING_CACHE_TTL) return;
  _panoMeetingFetchInflight = (async () => {
    try {
      const res = await fetch('/castintech-api/meetings?status=scheduled,active,voting', { credentials: 'include' });
      _panoMeetingLastFetch = Date.now(); // 4xx/5xx'te bile pencereyi aç (spam kes)
      if (!res.ok) return;
      const data = await res.json() as { meetings?: PanoMeeting[] };
      const next = Array.isArray(data?.meetings) ? data.meetings : [];
      // Sadece veri değişince re-render (id+status imzası).
      const prevSig = _panoMeetings.map(m => m.id + ':' + m.status).join(',');
      const nextSig = next.map(m => m.id + ':' + m.status).join(',');
      _panoMeetings = next;
      if (prevSig !== nextSig && state.currentPage === 'pano') {
        queueMicrotask(() => { try { renderPanoMeetingStrip(); } catch { /* ignore */ } });
      }
    } catch { /* fail soft */ }
    finally { _panoMeetingFetchInflight = null; }
  })();
}

// En öncelikli toplantıyı seç: active > voting > en yakın scheduled.
// API zaten scheduled_start DESC + status önceliği sıralı veriyor; biz yine
// de defensive olarak status'e göre ayıklıyoruz.
function pickPanoMeeting(): PanoMeeting | null {
  if (!_panoMeetings.length) return null;
  const live = _panoMeetings.find(m => m.status === 'active')
    || _panoMeetings.find(m => m.status === 'voting');
  if (live) return live;
  // En yakın gelecekteki scheduled (yoksa en yakın geçmiş scheduled).
  const now = Date.now();
  const scheduled = _panoMeetings.filter(m => m.status === 'scheduled');
  if (!scheduled.length) return null;
  const upcoming = scheduled
    .filter(m => new Date(m.scheduled_start).getTime() >= now)
    .sort((a, b) => new Date(a.scheduled_start).getTime() - new Date(b.scheduled_start).getTime());
  if (upcoming.length) return upcoming[0];
  // Hepsi geçmişte ise en yeni olanı göster.
  return scheduled.slice().sort((a, b) =>
    new Date(b.scheduled_start).getTime() - new Date(a.scheduled_start).getTime())[0];
}

function fmtMeetingTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const sameDay = (() => {
    const n = new Date();
    return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
  })();
  const hhmm = d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', hour12: false });
  if (sameDay) return hhmm;
  const dm = d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
  return `${dm} ${hhmm}`;
}

export function renderPanoMeetingStrip(): void {
  const el = document.getElementById('pano-meeting-strip');
  if (!el) return;
  const m = pickPanoMeeting();
  if (!m) { el.innerHTML = ''; return; }

  const isLive = m.status === 'active' || m.status === 'voting';
  const startIso = m.actual_start || m.scheduled_start;
  const when = fmtMeetingTime(startIso);
  const title = escHtml((m.title || 'Toplantı').substring(0, 70));

  // Anti-slop: mor yok. Aktif → emerald (#22c55e), yaklaşan → sapphire (#60a5fa).
  const accent = isLive ? '#22c55e' : '#60a5fa';
  const icon = isLive ? '🟢' : '📅';
  const label = isLive
    ? (m.status === 'voting' ? 'Oylanıyor' : 'Aktif toplantı')
    : 'Yaklaşan toplantı';
  const pulse = isLive
    ? '<span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:#22c55e;box-shadow:0 0 6px #22c55e;margin-right:6px"></span>'
    : '';

  el.innerHTML = `
    <button type="button" data-action="open-meetings"
      style="display:flex;align-items:center;gap:8px;width:100%;text-align:left;cursor:pointer;
             padding:8px 12px;border-radius:8px;font-size:12px;line-height:1.3;
             background:${isLive ? 'rgba(34,197,94,.08)' : 'rgba(96,165,250,.07)'};
             border:1px solid ${isLive ? 'rgba(34,197,94,.35)' : 'rgba(96,165,250,.3)'};
             color:var(--text,#e2e8f0)">
      ${pulse}<span style="font-size:13px">${icon}</span>
      <span style="font-weight:600;color:${accent}">${escHtml(label)}:</span>
      <span style="font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;min-width:0">${title}</span>
      <span style="opacity:.7;font-variant-numeric:tabular-nums;white-space:nowrap">${escHtml(when)}</span>
      <span style="opacity:.5;white-space:nowrap">›</span>
    </button>`;
}

// Strip click → meetings view. #pano-meeting-strip'e bir kez bind.
let _panoMeetingStripBound = false;
export function bindPanoMeetingStrip(): void {
  if (_panoMeetingStripBound) return;
  const el = document.getElementById('pano-meeting-strip');
  if (!el) return;
  el.addEventListener('click', (e) => {
    const t = asEl(e.target);
    if (t?.closest('[data-action="open-meetings"]')) navigate('meetings');
  });
  _panoMeetingStripBound = true;
}

// ── Kanban render ────────────────────────────────────────────────────

export function renderKanban(): void {
  // Faz 47.2 S2.A — Blocked reasons backend fetch (lazy, cached 60s).
  ensureBlockedReasonsLoaded();
  // Pano üstü toplantı şeridi — fetch (60s cache) + mevcut veriyle render.
  ensurePanoMeetingLoaded();
  renderPanoMeetingStrip();
  // Faz 50.A — Scroll position preserve. 20s refresh sırasında body.innerHTML
  // reset olduğu için kullanıcı pozisyon kaybediyordu (Faz 48.A virtualization
  // notunda defer edilmişti). Önce mevcut scrollY'leri yakala, render sonu restore.
  const savedScrolls = new Map<string, number>();
  const preBoard = document.getElementById('kanban-board');
  if (preBoard) {
    preBoard.querySelectorAll<HTMLElement>('.kanban-cards[data-col]').forEach(el => {
      const col = el.dataset.col;
      if (col) savedScrolls.set(col, el.scrollTop);
    });
  }
  // Faz 15 perf-opt: aMap 2 kez (filteredTasks + renderKanban) → 1 kez.
  // filteredTasks'a aMap parametre olarak geciliyor; search filtresi
  // aktifse o da ayni map'i kullanir.
  const aMap = buildAgentMap();
  const ts = filteredTasks(aMap);
  // Restore scroll'u render sonrası yapıyoruz (board.innerHTML yazıldıktan sonra).
  // Closure ile end-of-render path'inde uygulanır.
  const restoreScrolls = () => {
    if (savedScrolls.size === 0) return;
    const newBoard = document.getElementById('kanban-board');
    if (!newBoard) return;
    newBoard.querySelectorAll<HTMLElement>('.kanban-cards[data-col]').forEach(el => {
      const col = el.dataset.col;
      if (col && savedScrolls.has(col)) el.scrollTop = savedScrolls.get(col)!;
    });
  };
  // requestAnimationFrame ile bir frame sonra restore (DOM commit garantisi)
  if (savedScrolls.size > 0) requestAnimationFrame(restoreScrolls);

  const sub = document.getElementById('kanban-sub');
  const co = state.selectedCompanyId !== 'all' ? state.companies.find(c => c.id === state.selectedCompanyId) : null;
  // Ayrım notu: Kanban = Paperclip issue panosu (insan/ajan işleri).
  // Makine iş kuyruğu (central_brain.actions, claim/lease) ayrı: Central Brain → Actions.
  if (sub) sub.textContent = (co ? co.name + ' · ' : '') + ts.length
    + ' görev · Paperclip issue panosu — makine iş kuyruğu: Central Brain → Actions';

  const meta = document.getElementById('kf-meta');
  if (meta) {
    const active = Object.values(state.filterState).filter(v => v).length;
    const totalAll = state.selectedCompanyId !== 'all'
      ? state.tasks.filter(t => t.company_id === state.selectedCompanyId).length
      : state.tasks.length;
    meta.textContent = active ? `${ts.length}/${totalAll} (${active} filtre)` : `${ts.length} görev`;
  }

  const board = document.getElementById('kanban-board');
  if (!board) return;
  // Faz 48.A — Virtualization: >VIRT_THRESHOLD task'lı kolonlarda ilk
  // VIRT_INITIAL kart sync render edilir; geri kalan IntersectionObserver
  // sentinel ile scroll edildikçe chunk-render olur. <= threshold kolonlar
  // eski davranışta full render (geri uyumlu). 832 done + 203 backlog gibi
  // büyük kolonlarda ilk paint'i %85+ hızlandırır.
  board.innerHTML = KANBAN_COLS.map(col => {
    // Render katmanında da stabil sıralama: upstream sırası ne olursa olsun
    // kolon-içi kart sırası deterministik (kart zıplaması garantili çözülür).
    const colTasks = ts.filter(t => normStatus(t.status) === col.id).sort(compareTasksStable);
    // Faz 52 — Done kolonu lazy: initial fetch'te done hariç olduğu için
    // colTasks.length=0 olur. Kullanıcı "Yükle" butonuna basınca
    // getCompanyIssuesDone() çağrılır + state.tasks'a merge edilir.
    if (col.id === 'done' && colTasks.length === 0 && !_doneLoaded) {
      return `
        <div class="kanban-col">
          <div class="kanban-col-head">
            <div class="kanban-col-head-left">
              <div class="col-dot ${col.dotCls}"></div>
              <span class="col-name">${col.label}</span>
            </div>
            <span class="col-count" style="opacity:.5">lazy</span>
          </div>
          <div class="kanban-cards" data-col="${escAttr(col.id)}">
            <button class="kanban-load-done" data-action="load-done" style="margin:8px;padding:8px 14px;background:rgba(34,197,94,.08);border:1px dashed #22c55e;border-radius:6px;color:#86efac;font-size:11px;cursor:pointer;width:calc(100% - 16px)">📥 Tamamlananları yükle</button>
          </div>
        </div>`;
    }
    const useVirt = colTasks.length > VIRT_THRESHOLD;
    const initial = useVirt ? colTasks.slice(0, VIRT_INITIAL) : colTasks;
    const remaining = useVirt ? colTasks.length - VIRT_INITIAL : 0;
    // W2 — boş kolon context-aware mesaj: filtre aktifse "eşleşme yok",
    // değilse kolona özgü nötr mesaj ("—" yerine anlamlı).
    const _fs = state.filterState;
    const _filtersActive = !!(_fs.search || _fs.priority || _fs.assignee || _fs.age || state.agentFilter);
    const emptyMsg = _filtersActive
      ? 'Filtreye uyan görev yok'
      : (col.id === 'blocked' ? 'Bloklu iş yok 👍'
        : col.id === 'done' ? 'Henüz tamamlanan yok'
        : 'Görev yok');
    const cards = colTasks.length
      ? initial.map((t, i) => issueCard(t, aMap, i)).join('')
      : `<div class="kanban-empty">${emptyMsg}</div>`;
    const sentinel = useVirt
      ? `<div class="kanban-virt-sentinel" data-col="${escAttr(col.id)}" data-offset="${VIRT_INITIAL}">⋯ ${remaining} kart daha · scroll</div>`
      : '';
    return `
      <div class="kanban-col">
        <div class="kanban-col-head">
          <div class="kanban-col-head-left">
            <div class="col-dot ${col.dotCls}"></div>
            <span class="col-name">${col.label}</span>
          </div>
          <span class="col-count" aria-label="${colTasks.length} görev">${colTasks.length}</span>
        </div>
        <div class="kanban-cards" data-col="${escAttr(col.id)}">${cards}${sentinel}</div>
      </div>`;
  }).join('');

  // Virtualization observer wire — yeni eklenen sentinel'leri izle.
  setupVirtualizationObserver(ts, aMap);
}

// Faz 48.A — Virtualization config + helpers.
const VIRT_THRESHOLD = 100; // bu sayıdan büyük kolonlar virtualize.
const VIRT_INITIAL = 50;     // ilk paint kart sayısı.
const VIRT_CHUNK = 50;       // her sentinel hit'te yeni eklenecek kart.

let _virtObserver: IntersectionObserver | null = null;
function setupVirtualizationObserver(allTasks: Issue[], aMap: Record<string, Agent>): void {
  const sentinels = document.querySelectorAll<HTMLElement>('.kanban-virt-sentinel');
  if (!sentinels.length) return;
  if (_virtObserver) _virtObserver.disconnect();
  _virtObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const sentinel = entry.target as HTMLElement;
      const colId = sentinel.dataset.col;
      const offset = parseInt(sentinel.dataset.offset || '0', 10);
      if (!colId || !Number.isFinite(offset)) continue;
      // Sadece bu kolonun task'ları
      const colTasks = allTasks.filter(t => normStatus(t.status) === colId);
      const next = colTasks.slice(offset, offset + VIRT_CHUNK);
      if (!next.length) {
        sentinel.remove();
        continue;
      }
      const html = next.map((t, i) => issueCard(t, aMap, offset + i)).join('');
      sentinel.insertAdjacentHTML('beforebegin', html);
      const newOffset = offset + next.length;
      if (newOffset >= colTasks.length) {
        sentinel.remove();
      } else {
        sentinel.dataset.offset = String(newOffset);
        sentinel.textContent = `⋯ ${colTasks.length - newOffset} kart daha · scroll`;
      }
    }
  }, { rootMargin: '200px' });
  sentinels.forEach(s => _virtObserver!.observe(s));
}

function issueCard(t: Issue, aMap: Record<string, Agent>, i: number): string {
  const a = aMap[t.assigneeAgentId || t.assignee_id || ''];
  const gem = a ? getGem(a.name) : null;
  const prio = (t.priority || '').toLowerCase();
  const isActive = state.openIssueId === t.id;
  // Faz 47.2 S2.A — Blocked reason inline (son comment snippet, max 80ch).
  const isBlocked = normStatus(t.status) === 'blocked';
  const blockReason = isBlocked && _blockedReasons[t.id]
    ? _blockedReasons[t.id].substring(0, 80) + (_blockedReasons[t.id].length > 80 ? '…' : '')
    : '';
  // Faz 10 PR3 — onclick=openDetail(...) yerine data-action+data-task-id;
  // bindPanoDynamic() #kanban-board delegated listener'ina baglar.
  // W2 — klavye erişilebilirliği: kart focuslanabilir (tabindex) + role/aria;
  // Enter/Space ile açılma bindKanbanDelegation'da ele alınır.
  return `
    <div class="issue-card priority-${prio} ${isActive ? 'active-detail' : ''}"
         style="animation-delay:${i * 40}ms"
         tabindex="0" role="button" aria-label="${escAttr((t.identifier ? t.identifier + ' — ' : '') + (t.title || 'Görev'))}"
         data-action="open-detail" data-task-id="${escAttr(t.id)}">
      <div class="ic-top">
        <span class="ic-id">${escHtml(t.identifier || '')}</span>
        ${prio === 'high' || prio === 'urgent' ? '<span style="font-size:10px" aria-label="yüksek öncelik">🔴</span>' : ''}
      </div>
      <div class="ic-title">${escHtml((t.title || 'Görev').substring(0, 60))}</div>
      ${isBlocked ? `<div class="ic-block-reason" title="${escAttr(_blockedReasons[t.id] || 'Sebep için tıkla')}">🚫 ${blockReason ? escHtml(blockReason) : '<span style="opacity:.6">sebep için tıkla</span>'}</div>` : ''}
      <div class="ic-bottom">
        ${gem ? `
          <div class="ic-agent">
            <div class="ic-agent-av ${gem.cls}">
              <div class="ic-agent-av-bg"></div>
              <div class="ic-agent-av-letter">${gem.name[0]}</div>
            </div>
            <span class="ic-agent-name">${gem.name}</span>
          </div>` : '<div></div>'}
        <span class="ic-time">${ago(t.updatedAt || t.created_at || t.updatedAt)}</span>
      </div>
    </div>`;
}

// ── Yeni Gorev modal + agent dispatch ────────────────────────────────

export function openNewIssue(opts?: NewIssueOpts): void {
  const overlay = $('modal-overlay');
  const titleEl = $('ni-modal-title');
  const titleInput = $<HTMLInputElement>('ni-title');
  state.niPrefill = opts && opts.agentId ? { ...opts } : null;

  // Şirket dropdown
  const coSel = $<HTMLSelectElement>('ni-company');
  if (!coSel || !overlay || !titleEl || !titleInput) return;
  const targetCo = state.niPrefill ? state.niPrefill.companyId : state.selectedCompanyId;
  coSel.innerHTML = state.companies.map(c =>
    `<option value="${c.id}" ${c.id === targetCo ? 'selected' : ''}>${c.name}</option>`
  ).join('');

  // Şirket seçince ajanları güncelle (prefill yokken)
  coSel.onchange = () => updateAgentDropdown(coSel.value);
  updateAgentDropdown(coSel.value || state.companies[0]?.id || '');

  if (state.niPrefill) {
    coSel.value = state.niPrefill.companyId;
    coSel.disabled = true;
    const ag = $<HTMLSelectElement>('ni-agent');
    if (ag) {
      ag.value = state.niPrefill.agentId;
      ag.disabled = true;
    }
    titleEl.textContent = `📨 ${state.niPrefill.agentName || 'Ajan'} için komut`;
    titleInput.placeholder = `${state.niPrefill.agentName || 'Ajan'} için komut...`;
  } else {
    coSel.disabled = false;
    const ag = $<HTMLSelectElement>('ni-agent');
    if (ag) ag.disabled = false;
    titleEl.textContent = 'Yeni Görev';
    titleInput.placeholder = 'Görevi kısaca açıkla...';
  }

  titleInput.value = '';
  const descEl = $<HTMLTextAreaElement>('ni-desc');
  if (descEl) descEl.value = '';
  overlay.classList.add('visible');
  overlay.setAttribute('aria-hidden', 'false');
  setTimeout(() => titleInput.focus(), 100);
}

export function dispatchToAgent(agentName: string): void {
  if (!agentName || state.dispatchInFlight) return;
  const ag = state.agentsFull.find(a => (a.name || '').toLowerCase() === agentName.toLowerCase())
          || state.agentsFull.find(a => (a.slug || '').toLowerCase() === agentName.toLowerCase());
  if (!ag || !ag.id) {
    toast('Ajanın Paperclip ID\'si yok — komut gönderilemez', 'error');
    return;
  }
  // Şirket adından id'ye çevir
  const co = state.companies.find(c => c.name === ag.company)
          || state.companies.find(c => (c.slug || '').toLowerCase() === (ag.company || '').toLowerCase());
  if (!co) {
    toast('Şirket bulunamadı: ' + (ag.company || '?'), 'error');
    return;
  }
  openNewIssue({ companyId: co.id, agentId: ag.id, agentName: ag.name });
}

export function updateAgentDropdown(companyId: string): void {
  const sel = $<HTMLSelectElement>('ni-agent');
  if (!sel) return;
  const compAgents = state.agents.filter(a => a.companyId === companyId || a.company_id === companyId);
  sel.innerHTML = '<option value="">— Atama yok —</option>'
    + compAgents.map(a => `<option value="${a.id}">${getGem(a.name).name}</option>`).join('');
}

export function closeNewIssue(): void {
  const ov = $('modal-overlay');
  if (ov) {
    ov.classList.remove('visible');
    ov.setAttribute('aria-hidden', 'true');
  }
  // Prefill state temizle — bir sonraki açılış normal moda döner
  state.niPrefill = null;
  const co = $<HTMLSelectElement>('ni-company');
  if (co) co.disabled = false;
  const ag = $<HTMLSelectElement>('ni-agent');
  if (ag) ag.disabled = false;
}

export function handleModalOverlayClick(e: MouseEvent): void {
  if (e.target === $('modal-overlay')) closeNewIssue();
}

export async function submitNewIssue(): Promise<void> {
  if (state.dispatchInFlight) return;
  const title = ($<HTMLInputElement>('ni-title')?.value || '').trim();
  if (!title) { toast('Başlık zorunlu', 'error'); return; }
  const coId = $<HTMLSelectElement>('ni-company')?.value || '';
  const agentId = $<HTMLSelectElement>('ni-agent')?.value || '';
  const priority = $<HTMLSelectElement>('ni-priority')?.value || '';
  const description = ($<HTMLTextAreaElement>('ni-desc')?.value || '').trim();
  const fromDrawer = !!state.niPrefill;
  const agentLabel = state.niPrefill?.agentName;
  // Faz 3 — kanal kaynağını description'a yaz (Telegram'ın 'Telegram talebi' prefix'iyle paralel)
  const fullDesc = description ? `[dashboard] ${description}` : '[dashboard]';

  const btn = $<HTMLButtonElement>('ni-submit');
  state.dispatchInFlight = true;
  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Gönderiliyor...';
  }

  try {
    const r = await Api.postIssue(coId, {
      title,
      description: fullDesc,
      status: 'todo',
      priority,
      assigneeAgentId: agentId || undefined,
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${r.status}`);
    }
    const created = await r.json().catch(() => ({}));
    const idLabel = created.identifier || (created.id ? created.id.substring(0, 8) : '');
    closeNewIssue();
    if (fromDrawer && agentLabel) {
      toast(`✓ ${agentLabel}'a komut gönderildi${idLabel ? ' (' + idLabel + ')' : ''}`, 'success');
    } else {
      toast('Görev oluşturuldu ✓', 'success');
    }
    await refresh();
  } catch (e) {
    toast('Komut gönderilemedi: ' + (e as Error).message, 'error');
  } finally {
    state.dispatchInFlight = false;
    if (btn) {
      btn.disabled = false;
      btn.textContent = 'Görevi Oluştur';
    }
  }
}

// ── Faz 10 PR2 — New-issue modal statik click bind ──────────────────
// HTML inline onclick handler'lari silindi; main.js bootstrap bu fn'i cagirir.
let _niModalBound = false;
export function bindNewIssueModalStatic() {
  if (_niModalBound) return;

  // Overlay click → close yalnizca overlay'in kendisine tiklayinca
  const overlay = document.getElementById('modal-overlay');
  if (overlay) overlay.addEventListener('click', (e) => {
    if (e.target === e.currentTarget) closeNewIssue();
  });

  // Close × button (modal header)
  const closeBtn = overlay ? overlay.querySelector('.modal-close') : null;
  if (closeBtn) closeBtn.addEventListener('click', () => closeNewIssue());

  // Submit button — Görevi Oluştur
  const submitBtn = document.getElementById('ni-submit');
  if (submitBtn) submitBtn.addEventListener('click', () => submitNewIssue());

  _niModalBound = true;
}

// ── Faz 10 PR3 — Pano statik handler'lar ────────────────────────────
// 7 inline onclick/oninput/onchange handler dashboard.html'den silindi:
// - btn-suggestions (toggleSuggestions) + btn-new-issue (openNewIssue)
// - 4 filter (kf-search oninput, kf-priority/assignee/age onchange) + clear
// Idempotency: _panoStaticBound flag.
let _panoStaticBound = false;
export function bindPanoStatic() {
  if (_panoStaticBound) return;

  // Suggestions toggle (Pano header)
  const sgBtn = document.getElementById('btn-suggestions');
  if (sgBtn) sgBtn.addEventListener('click', () => toggleSuggestions());

  // Yeni Görev button
  const niBtn = document.querySelector<HTMLElement>('.btn-new-issue');
  if (niBtn) niBtn.addEventListener('click', () => openNewIssue(undefined));

  // 4 filter input/select → applyFilters
  const kfSearch = document.getElementById('kf-search');
  if (kfSearch) kfSearch.addEventListener('input', () => applyFilters());
  for (const id of ['kf-priority', 'kf-assignee', 'kf-age']) {
    const el = document.getElementById(id);
    if (el) el.addEventListener('change', () => applyFilters());
  }

  // Clear filters button
  const clearBtn = document.querySelector('.kf-btn-clear');
  if (clearBtn) clearBtn.addEventListener('click', () => clearFilters());

  // Faz 50.A — restore localStorage filter state UI'a (boot'ta state zaten yüklü).
  restoreFilterUI();

  // Toplantı şeridi click → meetings view.
  bindPanoMeetingStrip();

  _panoStaticBound = true;
}

// ── Faz 10 PR3 — Kanban board dynamic delegation ────────────────────
// issueCard inline onclick=openDetail(...) yerine data-action="open-detail"
// + data-task-id pattern. #kanban-board container'a bir kez bind.
let _kanbanDelegated = false;
export function bindKanbanDelegation() {
  if (_kanbanDelegated) return;
  const board = document.getElementById('kanban-board');
  if (!board) return;
  board.addEventListener('click', async (e) => {
    const target = asEl(e.target);
    // Faz 52 — Done kolonu lazy load button
    const loadBtn = target?.closest<HTMLElement>('[data-action="load-done"]');
    if (loadBtn) {
      if (_doneLoadInflight) return;
      _doneLoadInflight = true;
      loadBtn.textContent = '⏳ Yükleniyor...';
      try {
        const dones: Issue[] = [];
        for (const c of state.companies) {
          try {
            const r = await Api.getCompanyIssuesDone(c.id, 200);
            const d = await r.json() as { issues?: Issue[] } | Issue[];
            const arr: Issue[] = (Array.isArray(d) ? d : (d.issues || []));
            for (const t of arr) {
              dones.push({ ...t, company_name: c.name, company_id: c.id });
            }
          } catch { /* skip company */ }
        }
        // Merge — duplicate ID'leri filtrele
        const existingIds = new Set(state.tasks.map(t => t.id));
        const newDones = dones.filter(t => !existingIds.has(t.id));
        state.tasks = [...state.tasks, ...newDones].sort(compareTasksStable);
        _doneLoaded = true;
        renderKanban();
      } finally {
        _doneLoadInflight = false;
      }
      return;
    }
    const card = target?.closest<HTMLElement>('[data-action="open-detail"]');
    if (!card || !board.contains(card)) return;
    const taskId = card.dataset.taskId;
    if (taskId) openDetail(taskId);
  });
  // W2 — klavye: focuslanmış kartta Enter/Space → detay aç (a11y).
  board.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const card = asEl(e.target)?.closest<HTMLElement>('[data-action="open-detail"]');
    if (!card || !board.contains(card)) return;
    e.preventDefault();
    const taskId = card.dataset.taskId;
    if (taskId) openDetail(taskId);
  });
  _kanbanDelegated = true;
}
